import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search, Filter, X, Bookmark, BookmarkCheck, MapPin, Building2, GraduationCap,
  Calendar, Users, IndianRupee, ArrowUpDown, ChevronLeft, ChevronRight, Loader2,
  ExternalLink, FileText, Share2, Clock, Briefcase, AlertTriangle, Info, Monitor
} from 'lucide-react';
import { translateBatch } from '../services/translationService';
import axios from 'axios';
import AuthPromptModal from '../components/AuthPromptModal';
import useExtraJsonLd from '../hooks/useExtraJsonLd';
import { jobPostingJsonLd } from '../lib/seoSchemas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getDaysRemaining = (dateStr) => {
  if (!dateStr) return null;
  const now = new Date();
  const deadline = new Date(dateStr);
  const diff = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
  return diff;
};

const getDeadlineColor = (days) => {
  if (days === null) return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', days: null };
  if (days < 0) return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', days };
  if (days <= 5) return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300', days };
  if (days <= 15) return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', days };
  return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300', days };
};

const formatSalary = (min, max) => {
  if (!min && !max) return null;
  const fmt = (n) => {
    if (n >= 100000) return `${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)}L`;
    if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
    return n.toString();
  };
  if (min && max) return `${fmt(min)} - ${fmt(max)}`;
  if (min) return `${fmt(min)}+`;
  return `Up to ${fmt(max)}`;
};

const GovernmentJobs = () => {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [forcedAuth, setForcedAuth] = useState(false);
  const triggerAuthPrompt = () => setShowAuthPrompt(true);
  const { t, language } = useLanguage();

  // Shallow-routing for individual jobs: /schemes/jobs/:jobSlug.
  // Clicking a card pushes the slug URL; closing the modal pops it back to /schemes.
  // Direct visits to a slug URL auto-open the matching job in the modal.
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { jobSlug } = useParams();
  const autoOpenedSlugRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [savedJobIds, setSavedJobIds] = useState(new Set());
  const [translatedJobs, setTranslatedJobs] = useState({});

  // Filters — URL-driven so home page can deep-link (e.g. ?search=UPSC or ?category=Banking+and+Finance)
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const [category, setCategory] = useState(() => searchParams.get('category') || 'All');
  const [location, setLocation] = useState(() => searchParams.get('location') || 'All');
  const [sortBy, setSortBy] = useState('active');
  const [showFilters, setShowFilters] = useState(false);

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    categories: []
  });

  // Job detail modal
  const [selectedJob, setSelectedJob] = useState(null);

  // Recommended tech jobs for IT users
  const [techJobs, setTechJobs] = useState([]);
  const [showTechSection, setShowTechSection] = useState(false);
  const isITUser = user?.occupation === 'IT' || user?.occupation === 'IT/Technology';

  // Inject per-job JobPosting JSON-LD for the first 10 visible jobs
  // (keeps <head> small while giving Google enough rich data per page-view)
  useExtraJsonLd(
    (jobs || []).slice(0, 10).map(jobPostingJsonLd).filter(Boolean)
  );

  // Fetch filter options
  useEffect(() => {
    axios.get(`${API_URL}/api/jobs/categories`).then(res => {
      setFilterOptions(res.data);
    }).catch(() => {});
  }, []);

  // Keep filters in sync with URL params — lets the home page deep-link via
  // ?search=UPSC / ?category=Banking+and+Finance and have the user land on a
  // pre-filtered view. Navigating between home tiles without a full unmount
  // (Schemes.jsx stays mounted) still updates the filter.
  useEffect(() => {
    const s = searchParams.get('search') || '';
    const c = searchParams.get('category') || 'All';
    const l = searchParams.get('location') || 'All';
    setSearchQuery(s);
    setSearchInput(s);
    setCategory(c);
    setLocation(l);
    setPage(1);
  }, [searchParams]);

  // FORCED auth popup for guests after 1 minute on Jobs page (no cancel option)
  useEffect(() => {
    if (isAuthenticated) return;
    let elapsedMs = 0;
    let lastTick = Date.now();
    let intervalId = null;

    const tick = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        elapsedMs += now - lastTick;
        lastTick = now;
        if (elapsedMs >= 60_000) {
          setForcedAuth(true);
          setShowAuthPrompt(true);
          if (intervalId) clearInterval(intervalId);
        }
      } else {
        lastTick = Date.now();
      }
    };

    const onVisibility = () => {
      lastTick = Date.now();
    };

    intervalId = setInterval(tick, 1000);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated]);

  // Fetch recommended tech jobs for IT users
  useEffect(() => {
    if (!isITUser) return;
    axios.get(`${API_URL}/api/jobs`, { params: { category: 'Technology/IT', limit: 6, sort: 'latest' } })
      .then(res => {
        const tj = res.data.jobs || [];
        if (tj.length > 0) {
          setTechJobs(tj);
          setShowTechSection(true);
        }
      }).catch(() => {});
  }, [isITUser]);

  // Fetch saved job IDs
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API_URL}/api/jobs/saved`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const ids = new Set((res.data.saved_jobs || []).map(j => j.job_id));
      setSavedJobIds(ids);
    }).catch(() => {});
  }, [user]);

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sort: sortBy };
      if (searchQuery) params.search = searchQuery;
      if (category !== 'All') params.category = category;
      if (location !== 'All') params.location = location;

      const res = await axios.get(`${API_URL}/api/jobs`, { params });
      setJobs(res.data.jobs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 0);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, category, location, sortBy]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Translate dynamic job content when language changes
  useEffect(() => {
    if (language === 'en' || jobs.length === 0) {
      setTranslatedJobs({});
      return;
    }
    const translateJobs = async () => {
      const texts = [];
      jobs.forEach(j => {
        texts.push(j.title);
        if (j.organisation) texts.push(j.organisation);
      });
      try {
        const translated = await translateBatch(texts, language);
        const map = {};
        let idx = 0;
        jobs.forEach(j => {
          map[j.job_id] = {
            title: translated[idx] || j.title,
            organisation: j.organisation ? (translated[idx + 1] || j.organisation) : j.organisation,
          };
          idx += j.organisation ? 2 : 1;
        });
        setTranslatedJobs(map);
      } catch { setTranslatedJobs({}); }
    };
    translateJobs();
  }, [jobs, language]);

  // Search on Enter
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchQuery(searchInput);
  };

  const handleToggleSave = async (jobId, e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      if (savedJobIds.has(jobId)) {
        await axios.delete(`${API_URL}/api/jobs/save/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSavedJobIds(prev => { const s = new Set(prev); s.delete(jobId); return s; });
      } else {
        await axios.post(`${API_URL}/api/jobs/save`, { job_id: jobId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSavedJobIds(prev => new Set(prev).add(jobId));
      }
    } catch (err) {
      console.error('Error toggling save:', err);
    }
  };

  const handleShare = async (job, e) => {
    e.stopPropagation();
    const text = `${job.title} - ${job.organisation}\nApply: ${job.apply_url || job.source_url}`;
    if (navigator.share) {
      try { await navigator.share({ title: job.title, text }); } catch {}
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  const resetFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setCategory('All');
    setLocation('All');
    setSortBy('latest');
    setPage(1);
  };

  const activeFilterCount = [category, location].filter(f => f !== 'All').length + (searchQuery ? 1 : 0);

  // Gate card clicks for non-auth users
  const handleCardClick = (job) => {
    if (!isAuthenticated) { triggerAuthPrompt(); return; }
    setSelectedJob(job);
    // Shallow URL update — same Schemes route, just adds the job slug. Page does NOT
    // unmount/reload because both `/schemes` and `/schemes/jobs/:jobSlug` render
    // the same <Schemes /> element in App.js routing.
    if (job.slug) {
      navigate(`/schemes/jobs/${job.slug}`, { replace: false });
    }
  };

  // Closing the modal pops the URL back to the bare /schemes listing.
  const handleCloseModal = () => {
    setSelectedJob(null);
    if (routeLocation.pathname.startsWith('/schemes/jobs/')) {
      navigate('/schemes', { replace: false });
    }
  };

  // Direct visit to /schemes/jobs/:jobSlug — auto-open the matching job once jobs load.
  // We track the last auto-opened slug so legitimate URL changes (back/forward) work.
  useEffect(() => {
    if (!jobSlug) {
      autoOpenedSlugRef.current = null;
      return;
    }
    if (autoOpenedSlugRef.current === jobSlug) return;
    if (!jobs || jobs.length === 0) return;
    const match = jobs.find(j => j.slug === jobSlug);
    if (match) {
      setSelectedJob(match);
      autoOpenedSlugRef.current = jobSlug;
    } else {
      // Slug present in URL but not in current jobs page — fetch directly so deep
      // links to filtered/older jobs still work without forcing a paginated search.
      (async () => {
        try {
          const res = await axios.get(`${API_URL}/api/jobs/by-slug/${jobSlug}`);
          if (res?.data) {
            setSelectedJob(res.data);
            autoOpenedSlugRef.current = jobSlug;
          }
        } catch {
          // Slug not found — fall through silently; user sees the listing
          autoOpenedSlugRef.current = jobSlug;
        }
      })();
    }
  }, [jobSlug, jobs]);

  // Per-job SEO when a job is open via URL: dynamic title, description, JobPosting JSON-LD.
  useEffect(() => {
    if (!selectedJob || !jobSlug) return;
    const title = `${selectedJob.title} — KnowMySchemes`;
    const desc = (
      `${selectedJob.title} at ${selectedJob.organisation || selectedJob.department || 'Government of India'}. `
      + `Eligibility, last date, application link and complete details. ` +
      `KnowMySchemes aggregates from official sources — please verify on the official portal before applying.`
    ).slice(0, 158);
    const prevTitle = document.title;
    document.title = title;
    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute('content') : null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', desc);
    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== null) metaDesc.setAttribute('content', prevDesc);
    };
  }, [selectedJob, jobSlug]);

  // JobPosting JSON-LD for whichever job is open via URL — Google Jobs picks this up.
  useExtraJsonLd(
    selectedJob && jobSlug
      ? jobPostingJsonLd({
          ...selectedJob,
          url: `https://knowmyschemes.com/schemes/jobs/${selectedJob.slug || jobSlug}`,
        })
      : null
  );

  return (
    <div className="space-y-4" data-testid="government-jobs-section">
      {/* Recommended Tech Jobs for IT Users */}
      {showTechSection && techJobs.length > 0 && (
        <Card className="p-4 bg-gradient-to-r from-indigo-50 via-blue-50 to-cyan-50 border border-indigo-200 rounded-xl" data-testid="tech-jobs-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Monitor className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-indigo-900">{t('recommendedTechJobs')}</h3>
                <p className="text-[11px] text-indigo-600">{t('basedOnProfile')}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" className="text-xs border-indigo-300 text-indigo-700 h-7 px-2"
              onClick={() => { setCategory('Technology/IT'); setPage(1); }}
              data-testid="view-all-tech-jobs-btn"
            >
              {t('viewAll')}
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {techJobs.map((job, idx) => {
              const days = getDaysRemaining(job.last_date);
              const dc = getDeadlineColor(days);
              const salary = formatSalary(job.salary_min, job.salary_max);
              return (
                <div key={job.job_id} className="bg-white rounded-lg p-3 border border-indigo-100 hover:shadow-md cursor-pointer transition-all"
                  onClick={() => handleCardClick(job)} data-testid={`tech-job-card-${idx}`}
                >
                  <div className="flex items-start justify-between gap-1 mb-1.5">
                    <h4 className="text-xs font-semibold text-slate-800 line-clamp-2 leading-snug">{job.title}</h4>
                    <span className={`text-[10px] font-medium ${dc.text} whitespace-nowrap`}>{job.last_date ? (dc.days < 0 ? t('expired') : `${dc.days} ${t('daysLeft')}`) : t('rolling')}</span>
                  </div>
                  <p className="text-[11px] text-gray-500 mb-1.5 truncate">{job.organisation}</p>
                  <div className="flex items-center gap-2 text-[10px] text-gray-400">
                    {job.vacancies && <span>{job.vacancies.toLocaleString()} posts</span>}
                    {salary && <span>{salary}</span>}
                    <span className="ml-auto">{job.location}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Search */}
      <form onSubmit={handleSearchSubmit}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder={t('searchJobsPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10 pr-20 h-11 text-sm border-blue-200 focus:border-blue-500 focus:ring-blue-500"
            data-testid="search-jobs-input"
          />
          <Button type="submit" size="sm" className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-xs" data-testid="search-jobs-btn">
            {t('search')}
          </Button>
        </div>
      </form>

      {/* Filter Toggle + Sort */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="border-blue-300 text-blue-700 text-sm" data-testid="toggle-jobs-filters-btn">
            <Filter className="w-4 h-4 mr-1.5" />
            {t('filters')}
            {activeFilterCount > 0 && <Badge className="ml-1.5 bg-blue-600 text-white text-xs px-1.5 py-0">{activeFilterCount}</Badge>}
          </Button>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="text-blue-700 text-sm" data-testid="clear-jobs-filters-btn">
              <X className="w-4 h-4 mr-1" /> {t('clear')}
            </Button>
          )}
        </div>
        <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1); }}>
          <SelectTrigger className="w-[140px] h-9 text-sm border-gray-300" data-testid="sort-jobs-select">
            <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">{t('sortActive')}</SelectItem>
            <SelectItem value="latest">{t('sortExpected')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="p-4 border border-gray-200 shadow-lg bg-white rounded-xl" data-testid="jobs-filters-panel">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-600 flex items-center gap-1"><Briefcase className="w-3 h-3" /> {t('category')}</label>
              <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
                <SelectTrigger className="h-9 text-sm" data-testid="jobs-category-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All</SelectItem>
                  {filterOptions.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-600 flex items-center gap-1"><MapPin className="w-3 h-3" /> {t('location')}</label>
              <Select value={location} onValueChange={(v) => { setLocation(v); setPage(1); }}>
                <SelectTrigger className="h-9 text-sm" data-testid="jobs-location-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">{t('all')}</SelectItem>
                  <SelectItem value="All India">{t('allIndia')}</SelectItem>
                  <SelectItem value="Andhra Pradesh">{t('andhraPradesh')}</SelectItem>
                  <SelectItem value="Telangana">{t('telangana')}</SelectItem>
                  <SelectItem value="Karnataka">{t('karnataka')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>
      )}

      {/* Results count */}
      <p className="text-gray-600 text-sm" data-testid="jobs-results-count">
        {t('foundJobs')} <span className="font-semibold text-blue-700">{total}</span> {t('govJobs')}
      </p>

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : jobs.length > 0 ? (
        <>
          {/* Job Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jobs-grid">
            {jobs.map((job, idx) => {
              const days = getDaysRemaining(job.last_date);
              const dc = getDeadlineColor(days);
              const salary = formatSalary(job.salary_min, job.salary_max);
              const isSaved = savedJobIds.has(job.job_id);

              return (
                <Card
                  key={job.job_id}
                  className="group relative overflow-hidden bg-white hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100"
                  onClick={() => handleCardClick(job)}
                  data-testid={`job-card-${idx}`}
                >
                  {/* Deadline strip */}
                  <div className={`px-4 py-1.5 flex items-center justify-between ${job.source_type === 'expected' ? 'bg-amber-50 border-b border-amber-200' : `${dc.bg} border-b ${dc.border}`}`}>
                    <span className={`text-xs font-semibold ${job.source_type === 'expected' ? 'text-amber-700' : dc.text} flex items-center gap-1`}>
                      <Clock className="w-3 h-3" />
                      {job.source_type === 'expected' ? t('expected') : (job.last_date ? (dc.days < 0 ? t('expired') : `${dc.days} ${t('daysLeft')}`) : t('rolling'))}
                    </span>
                    {job.source_type === 'expected' ? (
                      <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 bg-amber-50" data-testid={`badge-expected-${idx}`}>{t('expected')}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-green-400 text-green-700 bg-green-50" data-testid={`badge-verified-${idx}`}>{t('sortActive')}</Badge>
                    )}
                  </div>

                  <div className="p-4">
                    {/* Title */}
                    <h3 className="font-bold text-sm leading-snug text-slate-800 mb-2 line-clamp-2 group-hover:text-blue-700 transition-colors" data-testid={`job-title-${idx}`}>
                      {translatedJobs[job.job_id]?.title || job.title}
                    </h3>

                    {/* Organisation */}
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                      <Building2 className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{translatedJobs[job.job_id]?.organisation || job.organisation || job.department}</span>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <Badge className="bg-blue-50 text-blue-700 text-[10px] font-medium">{job.category}</Badge>
                      {job.qualification && job.qualification !== 'Various' && (
                        <Badge className="bg-purple-50 text-purple-700 text-[10px] font-medium">{job.qualification}</Badge>
                      )}
                      <Badge className="bg-gray-50 text-gray-600 text-[10px] font-medium flex items-center gap-0.5">
                        <MapPin className="w-2.5 h-2.5" /> {job.location}
                      </Badge>
                    </div>

                    {/* Bottom row */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {job.vacancies && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" /> {job.vacancies.toLocaleString()}
                          </span>
                        )}
                        {salary && (
                          <span className="flex items-center gap-1">
                            <IndianRupee className="w-3 h-3" /> {salary}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={(e) => handleShare(job, e)} className="p-1.5 rounded-full hover:bg-gray-100 transition" data-testid={`share-job-${idx}`}>
                          <Share2 className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                        <button onClick={(e) => handleToggleSave(job.job_id, e)} className="p-1.5 rounded-full hover:bg-blue-50 transition" data-testid={`save-job-${idx}`}>
                          {isSaved ? <BookmarkCheck className="w-3.5 h-3.5 text-blue-600" /> : <Bookmark className="w-3.5 h-3.5 text-gray-400" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4" data-testid="jobs-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="jobs-prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-600">Page {page} of {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="jobs-next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <Card className="p-8 text-center border-none bg-white shadow-lg" data-testid="no-jobs-found">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-slate-700 mb-1">{t('noJobsFound')}</h3>
          <p className="text-gray-500 text-sm mb-4">{t('tryAdjustingJobs')}</p>
          <Button onClick={resetFilters} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="clear-all-jobs-filters-btn">
            {t('clearAllJobFilters')}
          </Button>
        </Card>
      )}

      {/* Job Detail Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={handleCloseModal} data-testid="job-detail-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <JobDetail job={selectedJob} isSaved={savedJobIds.has(selectedJob.job_id)} onToggleSave={handleToggleSave} onShare={handleShare} onClose={handleCloseModal} t={t} language={language} translatedJobs={translatedJobs} />
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="jobs-disclaimer">
        <div className="flex gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-amber-800 font-semibold mb-1">{t('disclaimer')}</p>
            <p className="text-xs text-amber-700 leading-relaxed">{t('jobsDisclaimer')}</p>
          </div>
        </div>
      </div>
      {/* Auth Prompt Modal — for non-signed-up users on card click or save */}
      <AuthPromptModal
        isOpen={showAuthPrompt}
        onClose={() => { if (!forcedAuth) setShowAuthPrompt(false); }}
        forced={forcedAuth}
      />
    </div>
  );
};

const JobDetail = ({ job, isSaved, onToggleSave, onShare, onClose, t, language, translatedJobs }) => {
  const days = getDaysRemaining(job.last_date);
  const dc = getDeadlineColor(days);
  const salary = formatSalary(job.salary_min, job.salary_max);
  const tj = translatedJobs?.[job.job_id] || {};

  return (
    <div data-testid="job-detail-content">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-start justify-between rounded-t-2xl z-10">
        <div className="flex-1 pr-4">
          <h2 className="text-lg font-bold text-slate-800 leading-snug" data-testid="job-detail-title">{tj.title || job.title}</h2>
          <p className="text-sm text-gray-500 mt-1">{tj.organisation || job.department || job.organisation}</p>
        </div>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" data-testid="close-job-detail">
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Expected recruitment notice */}
        {job.source_type === 'expected' && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="expected-notice">
            <Info className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              <strong>{t('expectedRecruitment')}</strong> — {t('expectedRecruitmentDesc')}
            </p>
          </div>
        )}

        {/* Deadline banner */}
        {job.source_type === 'expected' ? (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border-amber-200 border">
            <Calendar className="w-4 h-4 flex-shrink-0 text-amber-600" />
            <p className="text-sm font-semibold text-amber-700">Check official website for exact dates</p>
          </div>
        ) : (
          <div className={`flex items-center gap-2 p-3 rounded-lg ${dc.bg} ${dc.border} border`}>
            <Calendar className="w-4 h-4 flex-shrink-0" />
            <div>
              <p className={`text-sm font-semibold ${dc.text}`}>
                {job.last_date ? `${t('lastDate')}: ${new Date(job.last_date).toLocaleDateString(language === 'te' ? 'te-IN' : language === 'hi' ? 'hi-IN' : language === 'kn' ? 'kn-IN' : 'en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}` : t('rollingApplication')}
              </p>
              {days !== null && days >= 0 && <p className={`text-xs ${dc.text} opacity-80`}>{days} {t('daysRemaining')}</p>}
            </div>
          </div>
        )}

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-3">
          {job.organisation && (
            <InfoItem icon={Building2} label={t('organisation')} value={tj.organisation || job.organisation} />
          )}
          {job.category && (
            <InfoItem icon={Briefcase} label={t('category')} value={job.category} />
          )}
          {job.qualification && (
            <InfoItem icon={GraduationCap} label={t('qualification')} value={job.qualification} />
          )}
          {job.location && (
            <InfoItem icon={MapPin} label={t('location')} value={job.location} />
          )}
          {job.vacancies && (
            <InfoItem icon={Users} label={t('vacancies')} value={job.vacancies.toLocaleString()} />
          )}
          {salary && (
            <InfoItem icon={IndianRupee} label={t('salary')} value={salary} />
          )}
          {(job.age_limit_min || job.age_limit_max) && (
            <InfoItem icon={Info} label={t('ageLimit')} value={`${job.age_limit_min || '—'} to ${job.age_limit_max || '—'} ${t('years')}`} />
          )}
          {job.source && (
            <InfoItem icon={Info} label={t('source')} value={job.source} />
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          {job.notification_url && (
            <a href={job.notification_url} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid="view-notification-btn">
              <Button variant="outline" className="w-full border-blue-300 text-blue-700 hover:bg-blue-50">
                <FileText className="w-4 h-4 mr-2" /> {t('viewOfficialNotification')}
              </Button>
            </a>
          )}
          {job.apply_url && (
            <a href={job.apply_url} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid="apply-now-btn">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                <ExternalLink className="w-4 h-4 mr-2" /> {t('applyNow')}
              </Button>
            </a>
          )}
        </div>

        {/* Save / Share */}
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={(e) => onToggleSave(job.job_id, e)} data-testid="detail-save-job-btn">
            {isSaved ? <BookmarkCheck className="w-4 h-4 mr-2 text-blue-600" /> : <Bookmark className="w-4 h-4 mr-2" />}
            {isSaved ? t('saved') : t('saveJob')}
          </Button>
          <Button variant="outline" className="flex-1" onClick={(e) => onShare(job, e)} data-testid="detail-share-job-btn">
            <Share2 className="w-4 h-4 mr-2" /> {t('share')}
          </Button>
        </div>

        {/* Disclaimer */}
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-[11px] text-gray-500 leading-relaxed">
            <strong>{t('disclaimer')}:</strong> {t('jobsDisclaimer')}
          </p>
        </div>
      </div>
    </div>
  );
};

const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 p-2.5 bg-gray-50 rounded-lg">
    <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
    <div>
      <p className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium text-slate-700">{value}</p>
    </div>
  </div>
);

export default GovernmentJobs;
